import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProductOption, { useProductOptionForm } from '@hooks/useProductOption'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'DuplicateProductOptionModal'

type ModalCallbacks = {
  closeModal: () => void,
  duplicateProductOption: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const DuplicateProductOptionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, duplicateProductOption } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const productOption = digObject(modalPayload, 'productOption', {})

  const {
    callbacks: {
      duplicateProductOption: duplicateFn,
    },
    creating,
  } = useProductOption(productOption)

  const productOptionFormPayload = useProductOptionForm(
    { ...productOption, name: `${productOption.name} - Copy` },
    { validateOn: 'change' },
  )

  const {
    entityState,
    entityState: {
      name,
    },
    errors,
    handlers,
    saveEnabled,
  } = productOptionFormPayload

  // To be used with handleCallbackAction
  const duplicateProductOptionPayload = {
    callbacks: {
      action: duplicateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: 'Product Option Duplicated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Duplicate" titleSecondLine="Product Option" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Name" errorMessage={errors.name}>
            <input name="name" type="text" value={name} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => duplicateProductOption(duplicateProductOptionPayload)}
          size="large"
        >
          {saveEnabled ? 'Duplicate Product Option' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicateProductOptionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
